import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { MdPersonAdd, MdSearch, MdVisibility } from 'react-icons/md';
import { clientesAPI } from '../services/api';

const ClientsContainer = styled.div`
  max-width: 1400px;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${props => props.theme.spacing.xl};
`;

const Title = styled.h1`
  font-size: ${props => props.theme.fontSizes.xxxl};
  color: ${props => props.theme.colors.textPrimary};
  margin: 0;
`;

const Button = styled.button`
  padding: ${props => props.theme.spacing.md} ${props => props.theme.spacing.lg};
  background-color: ${props => props.theme.colors.primary};
  color: white;
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.fontSizes.sm};
  font-weight: ${props => props.theme.fontWeights.medium};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  transition: ${props => props.theme.transitions.fast};

  svg {
    font-size: 1.25rem;
  }

  &:hover {
    background-color: ${props => props.theme.colors.primaryDark};
    transform: translateY(-1px);
    box-shadow: ${props => props.theme.shadows.md};
  }
`;

const FilterSection = styled.div`
  background: ${props => props.theme.colors.backgroundLight};
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: ${props => props.theme.spacing.lg};
  margin-bottom: ${props => props.theme.spacing.xl};
  box-shadow: ${props => props.theme.shadows.sm};
  border: 1px solid ${props => props.theme.colors.border};
`;

const SearchContainer = styled.div`
  position: relative;
  max-width: 500px;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: ${props => props.theme.spacing.md};
  padding-left: 2.5rem;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.fontSizes.sm};
  background-color: ${props => props.theme.colors.background};
  transition: ${props => props.theme.transitions.fast};

  &:focus {
    border-color: ${props => props.theme.colors.primary};
    background-color: ${props => props.theme.colors.backgroundLight};
  }

  &::placeholder {
    color: ${props => props.theme.colors.textLight};
  }
`;

const SearchIcon = styled(MdSearch)`
  position: absolute;
  left: ${props => props.theme.spacing.md};
  top: 50%;
  transform: translateY(-50%);
  color: ${props => props.theme.colors.textLight};
  font-size: 1.25rem;
`;

const FilterButtons = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.md};
  margin-top: ${props => props.theme.spacing.md};
`;

const FilterButton = styled.button`
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.lg};
  background-color: ${props => props.active ? props.theme.colors.primary : props.theme.colors.backgroundLight};
  color: ${props => props.active ? 'white' : props.theme.colors.textPrimary};
  border: 1px solid ${props => props.active ? props.theme.colors.primary : props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.fontSizes.sm};
  font-weight: ${props => props.theme.fontWeights.medium};
  transition: ${props => props.theme.transitions.fast};

  &:hover {
    background-color: ${props => props.active ? props.theme.colors.primaryDark : props.theme.colors.background};
  }
`;

const ClientsListSection = styled.div`
  background: ${props => props.theme.colors.backgroundLight};
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: ${props => props.theme.spacing.xl};
  box-shadow: ${props => props.theme.shadows.sm};
  border: 1px solid ${props => props.theme.colors.border};
`;

const SectionTitle = styled.h2`
  font-size: ${props => props.theme.fontSizes.xl};
  color: ${props => props.theme.colors.textPrimary};
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Thead = styled.thead`
  background-color: ${props => props.theme.colors.background};
`;

const Th = styled.th`
  padding: ${props => props.theme.spacing.md};
  text-align: left;
  font-size: ${props => props.theme.fontSizes.sm};
  font-weight: ${props => props.theme.fontWeights.semibold};
  color: ${props => props.theme.colors.textSecondary};
  border-bottom: 1px solid ${props => props.theme.colors.border};
`;

const Td = styled.td`
  padding: ${props => props.theme.spacing.md};
  font-size: ${props => props.theme.fontSizes.sm};
  color: ${props => props.theme.colors.textPrimary};
  border-bottom: 1px solid ${props => props.theme.colors.borderLight};
`;

const StatusBadge = styled.span`
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.sm};
  border-radius: ${props => props.theme.borderRadius.sm};
  font-size: ${props => props.theme.fontSizes.xs};
  font-weight: ${props => props.theme.fontWeights.medium};
  background-color: ${props => {
    const status = props.status?.toLowerCase();
    if (status === 'al corriente') return props.theme.colors.success + '20';
    if (status === 'vencido') return props.theme.colors.danger + '20';
    if (status === 'pendiente') return props.theme.colors.warning + '20';
    if (status === 'sin recibos') return props.theme.colors.textSecondary + '20';
    return props.theme.colors.warning + '20';
  }};
  color: ${props => {
    const status = props.status?.toLowerCase();
    if (status === 'al corriente') return props.theme.colors.success;
    if (status === 'vencido') return props.theme.colors.danger;
    if (status === 'pendiente') return props.theme.colors.warning;
    if (status === 'sin recibos') return props.theme.colors.textSecondary;
    return props.theme.colors.warning;
  }};
`;

const ActionButton = styled.button`
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  background-color: ${props => props.theme.colors.primary};
  color: white;
  border-radius: ${props => props.theme.borderRadius.sm};
  font-size: ${props => props.theme.fontSizes.sm};
  font-weight: ${props => props.theme.fontWeights.medium};
  display: inline-flex;
  align-items: center;
  gap: ${props => props.theme.spacing.xs};
  cursor: pointer;
  transition: ${props => props.theme.transitions.fast};

  svg {
    font-size: 1rem;
  }

  &:hover {
    background-color: ${props => props.theme.colors.primaryDark};
  }
`;

const ClientNameLink = styled.button`
  color: ${props => props.theme.colors.textPrimary};
  font-size: ${props => props.theme.fontSizes.sm};
  font-weight: ${props => props.theme.fontWeights.medium};
  cursor: pointer;
  background: none;
  border: none;
  padding: 0;
  text-align: left;
  transition: ${props => props.theme.transitions.fast};

  &:hover {
    color: ${props => props.theme.colors.primary};
    text-decoration: underline;
  }
`;

const Pagination = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: ${props => props.theme.spacing.md};
  margin-top: ${props => props.theme.spacing.xl};
`;

const PageButton = styled.button`
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  background-color: ${props => props.active ? props.theme.colors.primary : props.theme.colors.backgroundLight};
  color: ${props => props.active ? 'white' : props.theme.colors.textPrimary};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.sm};
  font-size: ${props => props.theme.fontSizes.sm};
  font-weight: ${props => props.theme.fontWeights.medium};
  transition: ${props => props.theme.transitions.fast};
  cursor: pointer;

  &:hover:not(:disabled) {
    background-color: ${props => props.active ? props.theme.colors.primaryDark : props.theme.colors.background};
    border-color: ${props => props.theme.colors.primary};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Clients = () => {
  const navigate = useNavigate();
  const [filter, setFilter] = useState('Todos');
  const [searchTerm, setSearchTerm] = useState('');
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const clientsPerPage = 10;

  useEffect(() => {
    loadClientes();
  }, []);

  // Resetear a página 1 cuando cambian los filtros o búsqueda
  useEffect(() => {
    setCurrentPage(1);
  }, [filter, searchTerm]);

  const loadClientes = async () => {
    try {
      setLoading(true);
      const response = await clientesAPI.getAll();
      setClientes(response.data.data || []);
      setError(null);
    } catch (err) {
      setError(err.message || 'Error al cargar clientes');
      console.error('Error al cargar clientes:', err);
    } finally {
      setLoading(false);
    }
  };

  const getClientStatus = (cliente) => {
    // Sin recibos
    if (!cliente.total_recibos || cliente.total_recibos === 0) return 'Sin recibos';
    
    // Tiene recibos vencidos
    if (cliente.recibos_vencidos > 0) return 'Vencido';
    
    // Saldo completamente pagado
    if (parseFloat(cliente.saldo_pendiente || 0) === 0) return 'Al corriente';
    
    // Tiene saldo pendiente pero sin vencer
    if (parseFloat(cliente.saldo_pendiente || 0) > 0) return 'Pendiente';
    
    return 'Al corriente';
  };

  const filteredClients = clientes.filter(client => {
    const status = getClientStatus(client);
    const matchesFilter = filter === 'Todos' || status === filter;
    const matchesSearch = client.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (client.rfc && client.rfc.toLowerCase().includes(searchTerm.toLowerCase())) ||
                         (client.email && client.email.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesFilter && matchesSearch;
  });

  // Calcular paginación sobre clientes filtrados
  const indexOfLastClient = currentPage * clientsPerPage;
  const indexOfFirstClient = indexOfLastClient - clientsPerPage;
  const currentClients = filteredClients.slice(indexOfFirstClient, indexOfLastClient);
  const totalPages = Math.ceil(filteredClients.length / clientsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handlePrevious = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  // Generar números de página para mostrar
  const getPageNumbers = () => {
    const pages = [];
    const maxPagesToShow = 5;
    
    if (totalPages <= maxPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        pages.push('...');
        pages.push(currentPage - 1);
        pages.push(currentPage);
        pages.push(currentPage + 1);
        pages.push('...');
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  if (loading) {
    return (
      <ClientsContainer>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <p>Cargando clientes...</p>
        </div>
      </ClientsContainer>
    );
  }

  if (error) {
    return (
      <ClientsContainer>
        <div style={{ textAlign: 'center', padding: '40px', color: '#E57373' }}>
          <p>Error: {error}</p>
          <Button onClick={loadClientes}>Reintentar</Button>
        </div>
      </ClientsContainer>
    );
  }

  return (
    <ClientsContainer>
      <PageHeader>
        <Title>Gestión de Clientes</Title>
        <Button onClick={() => navigate('/clientes/nuevo')}>
          <MdPersonAdd />
          Nuevo Cliente
        </Button>
      </PageHeader>

      <FilterSection>
        <SearchContainer>
          <SearchIcon />
          <SearchInput
            type="text"
            placeholder="Buscar cliente por nombre, RFC o email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </SearchContainer>
        
        <FilterButtons>
          <FilterButton active={filter === 'Todos'} onClick={() => setFilter('Todos')}>
            Todos ({clientes.length})
          </FilterButton>
          <FilterButton active={filter === 'Al corriente'} onClick={() => setFilter('Al corriente')}>
            Al corriente ({clientes.filter(c => getClientStatus(c) === 'Al corriente').length})
          </FilterButton>
          <FilterButton active={filter === 'Pendiente'} onClick={() => setFilter('Pendiente')}>
            Pendiente ({clientes.filter(c => getClientStatus(c) === 'Pendiente').length})
          </FilterButton>
          <FilterButton active={filter === 'Vencido'} onClick={() => setFilter('Vencido')}>
            Vencido ({clientes.filter(c => getClientStatus(c) === 'Vencido').length})
          </FilterButton>
        </FilterButtons>
      </FilterSection>

      <ClientsListSection>
        <SectionTitle>Lista de Clientes ({filteredClients.length})</SectionTitle>
        
        <Table>
          <Thead>
            <tr>
              <Th>Nombre del Cliente</Th>
              <Th>RFC</Th>
              <Th>Teléfono</Th>
              <Th>Saldo Pendiente</Th>
              <Th>Estado</Th>
              <Th>Acciones</Th>
            </tr>
          </Thead>
          <tbody>
            {currentClients.length > 0 ? (
              currentClients.map(client => (
                <tr key={client.id}>
                  <Td>
                    <ClientNameLink onClick={() => navigate(`/clientes/${client.id}`)}>
                      {client.nombre}
                    </ClientNameLink>
                  </Td>
                  <Td>{client.rfc || 'N/A'}</Td>
                  <Td>{client.telefono || 'N/A'}</Td>
                  <Td>${(client.saldo_pendiente || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}</Td>
                  <Td>
                    <StatusBadge status={getClientStatus(client)}>
                      {getClientStatus(client)}
                    </StatusBadge>
                  </Td>
                  <Td>
                    <ActionButton onClick={() => navigate(`/clientes/${client.id}`)}>
                      <MdVisibility />
                      Ver Detalles
                    </ActionButton>
                  </Td>
                </tr>
              ))
            ) : (
              <tr>
                <Td colSpan="6" style={{ textAlign: 'center' }}>No se encontraron clientes</Td>
              </tr>
            )}
          </tbody>
        </Table>
        
        {totalPages > 1 && (
          <Pagination>
            <PageButton 
              onClick={handlePrevious} 
              disabled={currentPage === 1}
            >
              Anterior
            </PageButton>
            
            {getPageNumbers().map((page, index) => (
              page === '...' ? (
                <span key={`ellipsis-${index}`} style={{ padding: '0 8px' }}>...</span>
              ) : (
                <PageButton
                  key={page}
                  active={currentPage === page}
                  onClick={() => handlePageChange(page)}
                >
                  {page}
                </PageButton>
              )
            ))}
            
            <PageButton 
              onClick={handleNext} 
              disabled={currentPage === totalPages}
            >
              Siguiente
            </PageButton>
          </Pagination>
        )}
      </ClientsListSection>
    </ClientsContainer>
  );
};

export default Clients;
